import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaYoutube,
  FaArrowRight,
  FaShareAlt,
  FaFileAlt,
  FaTrophy,
  FaChess,
  FaQrcode,
  FaLink,
  FaEnvelope,
  FaWhatsapp,
  FaClock,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaGlobe,
  FaChessKnight,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { logAnalyticsEvent } from "../firebase/firebase";

const logoImage = "/logo.webp";
const qrCodeImage = "/qr.png";

const SocialsPage = () => {
  // State for QR code modal
  const [showQrCode, setShowQrCode] = useState(false);
  const [isSparkleActive, setIsSparkleActive] = useState(true);

  // Event countdown state
  const [eventCountdown, setEventCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    progressPercentage: 0,
  });

  // Track page view on component mount
  useEffect(() => {
    logAnalyticsEvent("page_view", {
      page_title: "SIT Chess Club Socials",
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    const sparkleInterval = setInterval(() => {
      setIsSparkleActive((prev) => !prev);
    }, 3000); // Toggle every 3 seconds

    return () => clearInterval(sparkleInterval);
  }, []);

  // Event countdown timer effect
  useEffect(() => {
    const eventDate = new Date("December 26, 2025 18:00:00").getTime();
    // Calculate total duration in seconds (from now until event date)
    const now = new Date().getTime();
    const totalDuration = eventDate - now;
    const totalDurationInSeconds = Math.floor(totalDuration / 1000);

    const updateEventCountdown = () => {
      const currentTime = new Date().getTime();
      const distance = eventDate - currentTime;

      if (distance < 0) {
        // Event date passed
        setEventCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          progressPercentage: 100,
        });
        clearInterval(timer);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Calculate progress percentage (how much time has elapsed)
      const elapsedTime = totalDurationInSeconds - Math.floor(distance / 1000);
      const progressPercentage = Math.min(
        100,
        Math.max(0, (elapsedTime / totalDurationInSeconds) * 100)
      );

      setEventCountdown({
        days,
        hours,
        minutes,
        seconds,
        progressPercentage,
      });
    };

    // Initial update
    updateEventCountdown();

    // Update every second
    const timer = setInterval(updateEventCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  // Social media links
  const socialLinks = [
    {
      name: "Join our WhatsApp Group",
      icon: <FaWhatsapp size={24} color="#25D366" />,
      url: "https://chat.whatsapp.com/JhpGI8Ya7Ss2YE9UyMFQyA",
      bgHover: "#e6f9ef",
      isSpecial: true,
    },
    {
      name: "Official Website",
      icon: <FaChess size={24} color="#C5A013" />,
      url: "https://www.sitchessclub.in/",
      bgHover: "#fcf6e2",
      isSpecial: true,
    },
    {
      name: "Follow us on Instagram",
      icon: <FaInstagram size={24} color="#C13584" />,
      url: "https://www.instagram.com/sitchessclub",
      bgHover: "#fcf1f6",
    },
    {
      name: "Like us on Facebook",
      icon: <FaFacebookF size={24} color="#1877F2" />,
      url: "https://www.facebook.com/share/1GzXNtPALv/",
      bgHover: "#f1f6fc",
    },
    // {
    //   name: "Follow us on X",
    //   icon: <FaXTwitter size={24} color="#1DA1F2" />,
    //   url: "#",
    //   bgHover: "#f1fafc"
    // },
    {
      name: "Connect on LinkedIn",
      icon: <FaLinkedinIn size={24} color="#0077B5" />,
      url: "https://www.linkedin.com/company/sit-chess-club",
      bgHover: "#f1f7fc",
    },
    // {
    //   name: "Watch our Tournament Videos",
    //   icon: <FaYoutube size={24} color="#FF0000" />,
    //   url: "https://youtube.com/sitchessclub",
    //   bgHover: "#fcf1f1"
    // },
    {
      name: "Email Us",
      icon: <FaEnvelope size={24} color="#EA4335" />,
      url: "mailto:sitchessclub2025@gmail.com",
      bgHover: "#fcf1f1",
    },
  ];

  // Resource links
  const resourceLinks = [
    {
      name: "Club Resources & Guidelines",
      icon: <FaFileAlt size={20} color="#0C2340" />,
      url: "https://sitchessclub.notion.site/Club-Resources-Guidelines-1ddfe5b021c080ebb996c2eb9a0c85c4",
      bgHover: "#f3f1fc",
    },
    // {
    //   name: "Tournament History & Results",
    //   icon: <FaTrophy size={20} color="#0C2340" />,
    //   url: "#",
    //   bgHover: "#f1fcf6"
    // },
    {
      name: "Chess Tips & Strategies",
      icon: <FaChess size={20} color="#0C2340" />,
      url: "https://sitchessclub.notion.site/Chess-Tips-Strategies-1ddfe5b021c0800398b0f5f0cb44df3c",
      bgHover: "#fcf8f1",
    },
  ];

  // Function to handle event button clicks
  const handleEventButtonClick = (buttonName, url) => {
    logAnalyticsEvent("event_button_click", {
      button_name: buttonName,
      button_url: url,
    });
  };

  // Function to handle social media link clicks
  const handleSocialLinkClick = (linkName, url) => {
    logAnalyticsEvent("social_link_click", {
      link_name: linkName,
      link_url: url,
    });
  };

  // Function to handle resource link clicks
  const handleResourceLinkClick = (resourceName, url) => {
    logAnalyticsEvent("resource_link_click", {
      resource_name: resourceName,
      resource_url: url,
    });
  };

  // Function to handle share button click
  const handleShare = async () => {
    logAnalyticsEvent("share_button_click");

    if (navigator.share) {
      try {
        await navigator.share({
          title: "SIT Chess Club Social Links",
          text: "Check out SIT Chess Club's social media and resources!",
          url: window.location.href,
        });
        logAnalyticsEvent("content_shared", { method: "web_share_api" });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
      logAnalyticsEvent("content_shared", { method: "clipboard_copy" });
    }
  };

  // Function to handle QR code button click
  const handleQrCodeShow = () => {
    setShowQrCode(true);
    logAnalyticsEvent("qr_code_viewed");
  };

  // Animation variants for smoother transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  // Animation variants for the event card
  const eventCardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
        delay: 0.3,
      },
    },
  };

  // Animation variants for event countdown
  const countdownItemVariants = {
    initial: { scale: 0.8, opacity: 0.5 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.8, opacity: 0.5 },
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-[#0C2340] to-[#183454] py-10 px-4 sm:px-6">
        <div className="max-w-md mx-auto">
          {/* Header Section */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              ease: "easeOut",
            }}
          >
            <motion.div
              className="relative w-32 h-32 mx-auto mb-6"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.7,
                ease: "easeOut",
                delay: 0.2,
              }}
            >
              <img
                src={logoImage}
                alt="SIT Chess Club"
                className="rounded-full w-full h-full object-cover"
              />
            </motion.div>
            <motion.h1
              className="font-heading font-bold text-2xl text-white mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Connect with SIT Chess Club
            </motion.h1>
            <motion.p
              className="font-body text-[#C5A013] text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              Official links and resources
            </motion.p>
          </motion.div>

          {/* Event Card */}
          <motion.div
            className="mb-10 overflow-hidden"
            variants={eventCardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="relative bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl overflow-hidden">
              {/* Chess piece decoration */}
              <motion.div
                className="absolute -top-4 -left-4 text-white/10 text-6xl transform rotate-12"
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ opacity: 1, rotate: 12 }}
                transition={{ delay: 0.8, duration: 1 }}
              >
                <FaChessKnight />
              </motion.div>

              {/* Event Header */}
              <div className="p-4 bg-gradient-to-r from-[#0C2340]/90 to-[#0C2340]/80 backdrop-blur-sm border-b border-white/10">
                <h2 className="font-heading font-bold text-2xl text-white mb-1">
                  Chess Arena
                </h2>
                <div className="flex items-center justify-between">
                  <p className="text-[#C5A013] text-sm font-medium flex items-center">
                    <FaGlobe className="mr-1.5" size={12} />
                    <span>chess.com</span>
                  </p>
                  <p className="text-white/80 text-xs flex items-center">
                    <FaCalendarAlt className="mr-1.5" size={12} />
                    <span>Dec 26, 2025 • 6:00 PM IST</span>
                  </p>
                </div>
              </div>

              {/* Event Countdown */}
              <div className="p-4">
                <div className="flex justify-between mb-1">
                  {/* Days */}
                  <div className="flex flex-col items-center">
                    <motion.div
                      className="bg-gradient-to-b from-[#0C2340] to-[#0C2340]/80 w-16 h-16 rounded-lg flex items-center justify-center shadow-inner border border-[#C5A013]/30"
                      key={`days-${eventCountdown.days}`}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={countdownItemVariants}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="font-mono font-bold text-2xl text-white">
                        {eventCountdown.days}
                      </span>
                    </motion.div>
                    <span className="text-white/80 text-xs mt-1">Days</span>
                  </div>

                  {/* Hours */}
                  <div className="flex flex-col items-center">
                    <motion.div
                      className="bg-gradient-to-b from-[#0C2340] to-[#0C2340]/80 w-16 h-16 rounded-lg flex items-center justify-center shadow-inner border border-[#C5A013]/30"
                      key={`hours-${eventCountdown.hours}`}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={countdownItemVariants}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="font-mono font-bold text-2xl text-white">
                        {eventCountdown.hours.toString().padStart(2, "0")}
                      </span>
                    </motion.div>
                    <span className="text-white/80 text-xs mt-1">Hours</span>
                  </div>

                  {/* Minutes */}
                  <div className="flex flex-col items-center">
                    <motion.div
                      className="bg-gradient-to-b from-[#0C2340] to-[#0C2340]/80 w-16 h-16 rounded-lg flex items-center justify-center shadow-inner border border-[#C5A013]/30"
                      key={`minutes-${eventCountdown.minutes}`}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={countdownItemVariants}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="font-mono font-bold text-2xl text-white">
                        {eventCountdown.minutes.toString().padStart(2, "0")}
                      </span>
                    </motion.div>
                    <span className="text-white/80 text-xs mt-1">Minutes</span>
                  </div>

                  {/* Seconds */}
                  <div className="flex flex-col items-center">
                    <motion.div
                      className="bg-gradient-to-b from-[#0C2340] to-[#0C2340]/80 w-16 h-16 rounded-lg flex items-center justify-center shadow-inner border border-[#C5A013]/30"
                      key={`seconds-${eventCountdown.seconds}`}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={countdownItemVariants}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="font-mono font-bold text-2xl text-white">
                        {eventCountdown.seconds.toString().padStart(2, "0")}
                      </span>
                    </motion.div>
                    <span className="text-white/80 text-xs mt-1">Seconds</span>
                  </div>
                </div>

                <div className="mt-3 mb-5 w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#C5A013] to-[#E6B91E]"
                    style={{ width: `${eventCountdown.progressPercentage}%` }}
                    transition={{
                      duration: 0.5,
                      ease: "easeOut",
                    }}
                  />
                </div>

                {/* Event Buttons */}
                <div className="flex flex-col space-y-3">
                  {/* <motion.a
                    href="https://lichess.org/tournament"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-[#693621] to-[#8A4C30] text-white px-4 py-3 rounded-md font-medium shadow-md border border-[#9A5C40]/30"
                    whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleEventButtonClick('Join Lichess Tournament', 'https://lichess.org/tournament')}
                  >
                    <img 
                      src="https://lichess1.org/assets/logo/lichess-favicon-32.png" 
                      alt="Lichess logo" 
                      className="h-5 w-5 mr-2" 
                    />
                    Join on Lichess
                  </motion.a> */}

                  <motion.a
                    href="https://chat.whatsapp.com/JhpGI8Ya7Ss2YE9UyMFQyA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-[#25D366] text-white px-4 py-3 rounded-md font-medium shadow-md"
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      handleEventButtonClick(
                        "Join WhatsApp Group",
                        "https://chat.whatsapp.com/JhpGI8Ya7Ss2YE9UyMFQyA"
                      )
                    }
                  >
                    <FaWhatsapp className="mr-2 text-lg" />
                    WhatsApp Group
                  </motion.a>

                  <motion.a
                    href="https://www.sitchessclub.in/event/opoxtxp1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-[#0C2340] text-white px-4 py-3 rounded-md font-medium shadow-md"
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      handleEventButtonClick(
                        "Event Details",
                        "https://www.sitchessclub.in/event/opoxtxp1"
                      )
                    }
                  >
                    Event Details
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Social Media Links Section */}
          <motion.div
            className="space-y-4 mb-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {socialLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center bg-white rounded-lg p-4 shadow-sm transition-all duration-300 hover:shadow-md ${
                  link.isSpecial ? "relative overflow-hidden" : ""
                }`}
                style={{
                  textDecoration: "none",
                  width: "100%",
                }}
                variants={itemVariants}
                whileHover={{
                  backgroundColor: link.bgHover,
                  scale: link.isSpecial ? 1.03 : 1.02,
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 17,
                  },
                }}
                whileTap={{ scale: 0.98 }}
                aria-label={link.name}
                onClick={() => handleSocialLinkClick(link.name, link.url)}
              >
                {link.isSpecial && (
                  <>
                    {/* Sparkle container */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: isSparkleActive ? 1 : 0.3,
                        scale: isSparkleActive ? 1 : 0.97,
                      }}
                      transition={{
                        opacity: { duration: 1.5, ease: "easeInOut" },
                        scale: { duration: 1.5, ease: "easeInOut" },
                      }}
                    >
                      {/* Top-left sparkle */}
                      <motion.div
                        className="absolute top-1 left-1 w-2 h-2 bg-green-300 rounded-full"
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.7, 1, 0.7],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      />
                      {/* Top-right sparkle */}
                      <motion.div
                        className="absolute top-2 right-2 w-1.5 h-1.5 bg-green-400 rounded-full"
                        animate={{
                          scale: [1, 1.7, 1],
                          opacity: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 1.7,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: 0.3,
                        }}
                      />
                      {/* Bottom-left sparkle */}
                      <motion.div
                        className="absolute bottom-1 left-3 w-1 h-1 bg-green-200 rounded-full"
                        animate={{
                          scale: [1, 1.8, 1],
                          opacity: [0.6, 1, 0.6],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: 0.7,
                        }}
                      />
                      {/* Bottom-right sparkle */}
                      <motion.div
                        className="absolute bottom-2 right-3 w-1.5 h-1.5 bg-green-300 rounded-full"
                        animate={{
                          scale: [1, 1.4, 1],
                          opacity: [0.7, 1, 0.7],
                        }}
                        transition={{
                          duration: 2.2,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: 0.5,
                        }}
                      />
                      {/* Subtle highlight overlay */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-green-50/20 to-green-100/30 rounded-lg" />
                    </motion.div>

                    {/* "New" badge */}
                    <div className="absolute -right-1 -top-1">
                      <motion.div
                        className="bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-tight"
                        animate={{
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      >
                        New
                      </motion.div>
                    </div>
                  </>
                )}

                <div className="mr-4 flex-shrink-0 z-10">{link.icon}</div>
                <span
                  className={`font-heading font-medium text-[#0C2340] z-10 ${
                    link.isSpecial ? "font-semibold" : ""
                  }`}
                >
                  {link.name}
                </span>
                <motion.div
                  className="ml-auto text-gray-400 z-10"
                  initial={{ x: -5, opacity: 0.7 }}
                  whileHover={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaArrowRight />
                </motion.div>
              </motion.a>
            ))}
          </motion.div>

          {/* Resources Links Section */}
          <motion.div
            className="mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <motion.h3
              className="font-heading font-medium text-white text-center mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Resources & Information
            </motion.h3>

            <motion.div
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              transition={{ delayChildren: 0.7 }}
            >
              {resourceLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.url}
                  className="flex items-center bg-white rounded-lg p-3 shadow-sm transition-all duration-300 hover:shadow-md"
                  style={{
                    textDecoration: "none",
                    width: "100%",
                  }}
                  variants={itemVariants}
                  whileHover={{
                    backgroundColor: link.bgHover,
                    scale: 1.02,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 17,
                    },
                  }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={link.name}
                  onClick={() => handleResourceLinkClick(link.name, link.url)}
                >
                  <div className="mr-3 flex-shrink-0">{link.icon}</div>
                  <span className="font-heading text-sm font-medium text-[#0C2340]">
                    {link.name}
                  </span>
                  <motion.div
                    className="ml-auto text-gray-400 text-sm"
                    initial={{ x: -5, opacity: 0.7 }}
                    whileHover={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaArrowRight />
                  </motion.div>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex justify-center space-x-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 1,
              type: "spring",
              stiffness: 200,
            }}
          >
            <motion.button
              onClick={handleShare}
              className="flex items-center justify-center bg-[#0C2340] text-white px-4 py-2 rounded-md hover:bg-[#183454] transition-colors duration-300"
              aria-label="Share this page"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaShareAlt className="mr-2" />
              <span className="text-sm font-heading">Share</span>
            </motion.button>

            <motion.button
              onClick={handleQrCodeShow}
              className="flex items-center justify-center bg-[#0C2340] text-white px-4 py-2 rounded-md hover:bg-[#183454] transition-colors duration-300"
              aria-label="Show QR Code"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaQrcode className="mr-2" />
              <span className="text-sm font-heading">QR Code</span>
            </motion.button>
          </motion.div>

          {/* Footer */}
          <motion.footer
            className="text-center text-white/60 text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <p className="mb-2">
              © {new Date().getFullYear()} SIT Chess Club. All rights reserved.
            </p>
            <a
              href="mailto:sitchessclub2025@gmail.com"
              className="text-[#C5A013] hover:text-white transition-colors duration-300"
            >
              sitchessclub2025@gmail.com
            </a>

            <div className="mt-4">
              <a
                href="https://www.sitchessclub.in/"
                className="flex items-center justify-center text-white/80 hover:text-white transition-colors duration-300 text-xs"
              >
                <FaLink className="mr-1 text-[8px]" />
                Back to Website
              </a>
            </div>
          </motion.footer>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQrCode && (
        <motion.div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowQrCode(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white p-6 rounded-lg max-w-xs w-full text-center"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
          >
            <h3 className="font-heading font-bold text-[#0C2340] mb-4">
              SIT Chess Club Links
            </h3>
            <div className="mb-4 bg-white p-2 rounded-md inline-block">
              <img
                src={qrCodeImage}
                alt="QR Code for SIT Chess Club Socials"
                className="w-[200px] h-[200px]"
              />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code to access our social links
            </p>
            <motion.button
              className="w-full py-2 bg-[#0C2340] text-white rounded-md hover:bg-[#183454] transition-colors duration-300"
              onClick={() => setShowQrCode(false)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default SocialsPage;
